// 2D vector math for the simulation.
//
// HARD RULE: no transcendentals (sin/cos/atan2/exp/pow) anywhere in this file or anything the sim
// imports. IEEE-754 specifies +, -, *, / and sqrt as correctly rounded, so they produce identical
// results across V8, JavaScriptCore and SpiderMonkey. The trig functions are NOT specified that way
// and genuinely differ between engines — using them here would make a Safari replay diverge from a
// Chrome replay and break score verification. Trig belongs in the renderer only.
//
// Math.hypot is also avoided: slower, and its accumulation order is implementation-defined.

export interface Vec2 {
  x: number
  y: number
}

export const vec = (x = 0, y = 0): Vec2 => ({ x, y })

export const set = (out: Vec2, x: number, y: number): Vec2 => {
  out.x = x
  out.y = y
  return out
}

export const copy = (out: Vec2, a: Vec2): Vec2 => set(out, a.x, a.y)

export const add = (out: Vec2, a: Vec2, b: Vec2): Vec2 => set(out, a.x + b.x, a.y + b.y)

export const sub = (out: Vec2, a: Vec2, b: Vec2): Vec2 => set(out, a.x - b.x, a.y - b.y)

export const scale = (out: Vec2, a: Vec2, s: number): Vec2 => set(out, a.x * s, a.y * s)

/** out += a * s — the accumulate-forces workhorse. */
export const addScaled = (out: Vec2, a: Vec2, s: number): Vec2 =>
  set(out, out.x + a.x * s, out.y + a.y * s)

export const len2 = (a: Vec2): number => a.x * a.x + a.y * a.y

export const len = (a: Vec2): number => Math.sqrt(len2(a))

/** Squared distance. Prefer this in neighbour loops — never take a sqrt to compare radii. */
export const dist2 = (a: Vec2, b: Vec2): number => {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return dx * dx + dy * dy
}

export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y

/** Zero-length input yields zero rather than NaN — sheep stacked exactly must not poison the sim. */
export const normalize = (out: Vec2, a: Vec2): Vec2 => {
  const l2 = len2(a)
  if (l2 === 0) return set(out, 0, 0)
  const inv = 1 / Math.sqrt(l2)
  return set(out, a.x * inv, a.y * inv)
}

/** Clamp magnitude to `max`, leaving direction intact. */
export const limit = (out: Vec2, a: Vec2, max: number): Vec2 => {
  const l2 = len2(a)
  if (l2 <= max * max || l2 === 0) return copy(out, a)
  const inv = max / Math.sqrt(l2)
  return set(out, a.x * inv, a.y * inv)
}

/** Rescale to exactly `mag`. Zero-length input yields zero. */
export const setMag = (out: Vec2, a: Vec2, mag: number): Vec2 => {
  const l2 = len2(a)
  if (l2 === 0) return set(out, 0, 0)
  const inv = mag / Math.sqrt(l2)
  return set(out, a.x * inv, a.y * inv)
}

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v

/**
 * Closest point on segment [a,b] to p, and its squared distance.
 * Used for both fence avoidance steering and collision resolution.
 */
export function closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2, out: Vec2): number {
  const abx = b.x - a.x
  const aby = b.y - a.y
  const denom = abx * abx + aby * aby
  let t = 0
  if (denom > 0) {
    t = clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / denom, 0, 1)
  }
  set(out, a.x + abx * t, a.y + aby * t)
  return dist2(p, out)
}

/**
 * Do the segments a1→a2 and b1→b2 cross?
 *
 * Orientation tests only — three subtractions and two multiplications each — so this is pure
 * `+ − ×` and safe in the sim path. No angles, no square roots, nothing implementation-defined.
 *
 * Collinear-overlapping segments report false. That is the useful answer here: the dog sliding
 * exactly along a fence line is not jumping it.
 */
export function segmentsCross(a1: Vec2, a2: Vec2, b1: Vec2, b2: Vec2): boolean {
  const d = (p: Vec2, q: Vec2, r: Vec2): number =>
    (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x)

  const d1 = d(b1, b2, a1)
  const d2 = d(b1, b2, a2)
  const d3 = d(a1, a2, b1)
  const d4 = d(a1, a2, b2)

  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
}
